import { h } from 'preact';
import type { PageONumberPriceEntry, PageProductPriceEntry } from '../JsonProvider';
import { resolveDisplayPrice, type PdpVariantData } from '../shopware/pdpData';
import { formatPrice, getFormattedReferencePrice } from '../shopware/shopwareUtils';
import { URLS } from '../Urls';
import type { Variant } from './addToCartData';

/** What the price line renders, independent of where the numbers came from (live /pdp-data or snapshot). */
export interface PriceView {
  /** e.g. "15,99 €" */
  unitPrice: string;
  /** Streichpreis, e.g. "21,99 €", or null */
  listPrice: string | null;
  /** e.g. "571,07 €/kg", or null */
  referencePrice: string | null;
  /** "ab" prefix: several buyable variants with a real price range */
  isFrom: boolean;
  /** PIM flag of the variant the price belongs to */
  hideBasePrice: boolean;
}

/**
 * Live mode (hero): cheapest priced variant out of the batched /pdp-data result, priced like the PDP
 * and the snapshot (resolveDisplayPrice: code-free promotion beats the rule list price). "ab" only for
 * a real price range, the snapshot's rule (PageProductPriceCollector: more than one priced variant AND
 * a higher one than the minimum), so two variants at the same price do not promise a cheaper option.
 */
export function priceViewFromPdp(variants: Variant[], pdp: Record<string, PdpVariantData>): PriceView | null {
  const priced = variants
    .map((variant) => {
      const data = pdp[variant.packsize.oNumber] ?? null;
      return { variant, data, price: resolveDisplayPrice(data) };
    })
    .filter(({ price }) => price.amount !== null)
    .sort((a, b) => (a.price.amount as number) - (b.price.amount as number));
  const cheapest = priced[0];
  if (!cheapest || cheapest.price.unitPrice === null) return null;
  const dearest = priced[priced.length - 1];

  return {
    unitPrice: cheapest.price.unitPrice,
    listPrice: cheapest.price.listPrice,
    referencePrice: getFormattedReferencePrice(cheapest.data?.product),
    isFrom: priced.length > 1 && (dearest.price.amount as number) > (cheapest.price.amount as number),
    hideBasePrice: cheapest.variant.packsize.hideBasePrice === true,
  };
}

/** Formatted price fields of a page-product-prices-json entry (product aggregate or single pack size). */
function snapshotPrices(
  entry: PageProductPriceEntry | PageONumberPriceEntry
): Pick<PriceView, 'unitPrice' | 'listPrice' | 'referencePrice'> {
  return {
    unitPrice: `${formatPrice(entry.price)} €`,
    listPrice: entry.listPrice != null ? `${formatPrice(entry.listPrice)} €` : null,
    referencePrice:
      entry.referencePrice != null && entry.referenceUnit ? `${formatPrice(entry.referencePrice)} €/${entry.referenceUnit}` : null,
  };
}

/**
 * Snapshot mode (overview): the product aggregate from page-product-prices-json (PR #1509). `isFrom`
 * comes from the snapshot (real price range, not just several variants); the PIM hideBasePrice flag is
 * looked up on the variant the "from" price belongs to.
 */
export function priceViewFromSnapshot(entry: PageProductPriceEntry, variants: Variant[]): PriceView {
  const variant = variants.find((v) => v.packsize.oNumber === entry.oNumber);
  return {
    ...snapshotPrices(entry),
    isFrom: entry.isFrom,
    hideBasePrice: variant?.packsize.hideBasePrice === true,
  };
}

/** Snapshot mode with a pack size preselection (ORT-3175): that pack's own price, never "ab". */
export function priceViewFromONumber(entry: PageONumberPriceEntry, variant: Variant): PriceView {
  return {
    ...snapshotPrices(entry),
    isFrom: false,
    hideBasePrice: variant.packsize.hideBasePrice === true,
  };
}

interface PriceLineProps {
  view: PriceView;
  /** Overview cards (ORT-2947): reference price in parentheses, no tax/shipping note. Set via data-compact on the slot. */
  compact?: boolean;
}

/**
 * Price line markup shared by hero and cards: optional "ab", Streichpreis (<s>, same classes as the PDP),
 * current price, reference price unless hidden via PIM, tax/shipping note unless compact.
 */
export const PriceLine = ({ view, compact = false }: PriceLineProps) => {
  const reference = !view.hideBasePrice && view.referencePrice ? view.referencePrice : null;
  return (
    <>
      <strong className="price text-nowrap">
        {view.listPrice && <s className="price-original me-2">{view.listPrice}</s>}
        {view.isFrom && <span className="price-original me-1">ab</span>}
        <span className={view.listPrice ? 'price-discounted' : 'price-current'}>{view.unitPrice}</span>
      </strong>
      {compact ? (
        reference && <small>({reference})</small>
      ) : (
        <small>
          {reference && <>{reference} </>}
          inkl. MwSt. zzgl. <a href={URLS.shippingInfo}>Versand</a>
        </small>
      )}
    </>
  );
};

/**
 * Loading state of the price line: same structure as the final line (and the Twig skeleton),
 * placeholder values only. The slot carries aria-busy="true" while this is shown; the shimmer
 * styling lives in _add-to-cart.scss.
 */
export const PriceLineSkeleton = () => (
  <>
    <strong className="price text-nowrap">&mdash;,&mdash; &euro;</strong>
    <small>&mdash;,&mdash; &euro;/kg inkl. MwSt. zzgl. Versand</small>
  </>
);
